import { Box, Modal } from '@mui/material'
import { memo, useEffect, useRef, useState } from 'react'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'

import { useSelector } from 'react-redux'
import cx from 'classnames'
import Keywords from './Keywords'
import Categories from './Categories'
import Rank from './Rank'
import PageTargeting from './PageTargeting'
import UnsavedChangesDialog from './UnsavedChangesDialog'
import CloseIcon from '../Icons/CloseIcon'

import useStyles from './style'
import { accountInfo } from '../../store/slice/user'

const PLATFORM = {
  BOL: 'Bol',
}
const LIMIT = {
  Bol: 100,
}

function CustomTabPanel({ children, value, index, ...other }) {
  const classes = useStyles()

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box className={classes.panel_wrapper}>{children}</Box>}
    </div>
  )
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  }
}

const ProductManageModal = ({ open, productData, handleClose = () => {} }) => {
  const classes = useStyles()
  const [activeTabIdx, setActiveTabIdx] = useState(0)
  const [showKeywordCountWarning, setShowKeywordCountWarning] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isUnsavedWarningOpen, setIsUnsavedWarningOpen] = useState(false)
  const [footerHeight, setFooterHeight] = useState(30)
  const [headerHeight, setHeaderHeight] = useState(0)
  const [tabsWrapperHeight, setTabsWrapperHeight] = useState(0)
  const [windowHeight, setWindowHeight] = useState(window.innerHeight)
  const headerRef = useRef(null)
  const tabsWrapperRef = useRef(null)
  const accountInfoData = useSelector(accountInfo)

  const handleCloseRequest = () => {
    if (hasUnsavedChanges) {
      setIsUnsavedWarningOpen(true)
    } else {
      handleClose()
    }
  }

  const handleDiscardChanges = () => {
    setIsUnsavedWarningOpen(false)
    handleClose()
  }

  const handleChange = (event, newValue) => {
    if (newValue !== 2) setFooterHeight(30)

    if (newValue === 2) {
      if (!accountInfoData?.featureRankTargeting || !accountInfoData?.featurePageTargeting) {
        if (!accountInfoData?.featureRankTargeting) {
          setActiveTabIdx(3)
        } else if (!accountInfoData?.featurePageTargeting) {
          setActiveTabIdx(2)
        }
      } else {
        setActiveTabIdx(newValue)
      }
    } else {
      setActiveTabIdx(newValue)
    }
  }

  useEffect(() => {
    if (!open) setActiveTabIdx(0)
  }, [])

  useEffect(() => {
    if (!headerRef.current) return

    const calcHeight = () => {
      setHeaderHeight(headerRef.current.offsetHeight)
    }

    calcHeight()

    window.addEventListener('resize', calcHeight)

    // eslint-disable-next-line consistent-return
    return () => window.removeEventListener('resize', calcHeight)
  }, [productData, activeTabIdx])

  useEffect(() => {
    if (!tabsWrapperRef.current) return

    const calcHeight = () => {
      setTabsWrapperHeight(tabsWrapperRef.current.offsetHeight)
      setWindowHeight(window.innerHeight)
    }

    calcHeight()

    window.addEventListener('resize', calcHeight)

    // eslint-disable-next-line consistent-return
    return () => window.removeEventListener('resize', calcHeight)
  }, [activeTabIdx, productData])

  return (
    <Modal
      open={open}
      onClose={handleCloseRequest}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
      className={classes.modal}
    >
      <Box
        className={cx(classes.wrapper, {
          [classes.xs]:
            (!!accountInfoData?.featureRankTargeting && !accountInfoData?.featurePageTargeting) ||
            (!accountInfoData?.featureRankTargeting && !!accountInfoData?.featurePageTargeting),
          [classes.xl]: !!accountInfoData?.featureRankTargeting && !!accountInfoData?.featurePageTargeting,
          [classes.rankWide]: activeTabIdx === 2,
        })}
      >
        <Box className={classes.wrapper_header} ref={headerRef}>
          <div>
            <div title={productData?.name} className={classes.modal_title}>
              <span>Product:</span>
              {productData?.name}
            </div>
            <div className={classes.modal_title}>
              <span>EAN:</span>
              {productData?.ean}
            </div>
            {showKeywordCountWarning && (
              <div className={classes.modal_warning}>
                <span>Warning:</span> More than {LIMIT[PLATFORM.BOL]} keywords selected. Not all keywords will be
                uploaded to&nbsp;{PLATFORM.BOL}.
              </div>
            )}
          </div>
          <button className={classes.close_button} onClick={handleCloseRequest} type="button">
            <CloseIcon />
          </button>
        </Box>
        <div
          className={classes.tabs_wrapper}
          style={{
            '--header-height': `${headerHeight}px`,
            '--body-height': `${tabsWrapperHeight}px`,
            '--footer-height': `${footerHeight}px`,
            '--window-height': `${windowHeight}px`,
          }}
          ref={tabsWrapperRef}
        >
          <Tabs value={activeTabIdx} onChange={handleChange} className={classes.tabs_list}>
            <Tab label="Keywords" {...a11yProps(0)} />
            <Tab label="Category" {...a11yProps(1)} />
            {!!accountInfoData?.featureRankTargeting && <Tab label="Rank targeting" {...a11yProps(2)} />}
            {!!accountInfoData?.featurePageTargeting && <Tab label="Page targeting" {...a11yProps(3)} />}
          </Tabs>
          <CustomTabPanel value={activeTabIdx} index={0}>
            <Keywords
              productData={productData}
              setShowWarning={setShowKeywordCountWarning}
              setHasUnsavedChanges={setHasUnsavedChanges}
            />
          </CustomTabPanel>
          <CustomTabPanel value={activeTabIdx} index={1}>
            <Categories
              productData={productData}
              setShowWarning={setShowKeywordCountWarning}
              setHasUnsavedChanges={setHasUnsavedChanges}
            />
          </CustomTabPanel>
          {!!accountInfoData?.featureRankTargeting && (
            <CustomTabPanel value={activeTabIdx} index={2}>
              <Rank
                productData={productData}
                setFooterHeight={setFooterHeight}
                setHasUnsavedChanges={setHasUnsavedChanges}
              />
            </CustomTabPanel>
          )}
          {!!accountInfoData?.featurePageTargeting && (
            <CustomTabPanel value={activeTabIdx} index={3}>
              <PageTargeting
                productData={productData}
                setFooterHeight={setFooterHeight}
                setHasUnsavedChanges={setHasUnsavedChanges}
              />
            </CustomTabPanel>
          )}
        </div>
        <UnsavedChangesDialog
          open={isUnsavedWarningOpen}
          onClose={() => setIsUnsavedWarningOpen(false)}
          onDiscard={handleDiscardChanges}
        />
      </Box>
    </Modal>
  )
}

export default memo(ProductManageModal)
